import styled from "styled-components";
import colors from "../../styles/colors";

/* ── Page shell ─────────────────────────────────────────────── */

export const DashboardPageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
`;

export const Toolbar = styled.div`
  width: 100%;
  height: 60px;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: ${colors.theme.yaelDark};
  padding: 0.4rem 1rem;
`;

export const Title = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 1.2rem;
  color: white;
`;

export const FiltersWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  color: white;

  .MuiInputBase-input {
    color: white;
    font-size: 0.8rem;
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: white !important;
  }

  .MuiNativeSelect-icon,
  svg {
    fill: white;
    color: white;
  }
`;

export const RangeButton = styled.div<{ active?: boolean }>`
  font-size: 0.85rem;
  padding: 0.4rem 0.8rem;
  background-color: ${(props) =>
    props.active ? colors.theme.yaelBlue10 : colors.theme.yaelGreen};
  min-width: 80px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  height: 24px;
  cursor: ${(props) => (props.active ? "default" : "pointer")};
  user-select: none;

  &:hover {
    background-color: ${(props) =>
      props.active ? colors.theme.yaelGreen10 : colors.theme.yaelGreen80};
  }
`;

/* ── Content area ───────────────────────────────────────────── */

export const ContentArea = styled.div`
  width: 100%;
  flex: 1;
  overflow-y: auto;
  min-height: 1px;
  padding: 20px 24px;
  box-sizing: border-box;
  background-color: #f0f2f5;
`;

export const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 1440px;
  margin: 0 auto;
  width: 100%;
`;

/* ── Section layout ─────────────────────────────────────────── */

export const SectionTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 500;
  color: ${colors.theme.yaelDark};
  margin: 0;
  padding: 4px;
  border-bottom: 1px solid #f2f2f2;
`;

/* KPI row — two grouped boxes side by side */
export const KpiGrid = styled.div`
  display: flex;
  gap: 14px;
  width: 100%;
  flex-wrap: wrap;
`;

export const KpiGroup = styled.div`
  display: flex;
  flex: 1;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1.5px solid rgba(0, 0, 0, 0.07);
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.07);
  min-width: 0;

  & > * {
    flex: 1;
    min-width: 0;
    box-shadow: none;
    border-color: transparent;
    background: #fafafa;
  }
`;

/* Charts — 3-column flowing grid */
export const ChartsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
  align-items: stretch;

  & > * {
    flex: 1 1 calc(33.333% - 8px);
    min-width: 300px;
  }
`;

export const Grid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
`;

/* ── Cards ──────────────────────────────────────────────────── */

export const CardContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 12px;
  padding: 14px 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  background: white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.11);
    transform: translateY(-1px);
  }
`;

export const IconWrapper = styled.div`
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  margin-right: 0.4rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.color || colors.theme.yaelBlue};

  & > svg {
    display: block;
    font-size: 22px;
  }
`;

export const CardTitle = styled.h3`
  color: rgb(150, 158, 172);
  font-size: 0.62rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 2px 0;
`;

export const CardValue = styled.p<{ valueColor?: string }>`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${(props) => props.valueColor || colors.theme.yaelDark};
  margin: 0;
  line-height: 1.2;
`;

export const CardSubtitle = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  color: ${colors.theme.yaelGrey};
  margin-top: 3px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

/* ── Chart containers ───────────────────────────────────────── */

export const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-radius: 12px;
  padding: 16px 18px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  background: white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
`;

export const ChartTitle = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding-bottom: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${colors.theme.yaelDark};
  border-bottom: 2px solid #f0f2f5;
`;

/* ── Tables ─────────────────────────────────────────────────── */

export const TableContainer = styled(ChartContainer)`
  overflow-x: auto;
`;

export const StyledTable = styled.table`
  width: 100%;
  text-align: left;
  border-collapse: collapse;
`;

export const TableHeader = styled.thead`
  background-color: #f7fafc;
`;

export const TableRow = styled.tr`
  border-bottom: 1px solid #f2f2f2;
  &:last-child {
    border-bottom: none;
  }
`;

export const TableHeaderCell = styled.th`
  padding: 0.75rem 1rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.theme.yaelGrey};
`;

export const TableCell = styled.td`
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: ${colors.theme.yaelDark};
  &.font-mono {
    font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier,
      monospace;
  }
  &.status-success {
    color: ${colors.theme.yaelGreen};
  }
  &.status-failed {
    color: ${colors.theme.yaelOrange};
  }
  &.status-CALC_ERROR {
    color: ${colors.theme.yaelOrange};
  }
  &.status-exception {
    color: ${colors.theme.yaelGrey};
  }
`;
